import React from "react";
import styles from "./IncomingCallContent.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import { TIncomingCall } from "../../../types/callCenter.types";
import HotlineQuestionnaireNotice from "../../hotlineQuestionnaireNotice/HotlineQuestionnaireNotice";

interface IIncomingCallContentProps {
  call: TIncomingCall;
  onAnswer: () => void;
  onReject: () => void;
  /** Inline error from take-inquiry / validation (no browser alert). */
  answerError?: string | null;
  answerLoading?: boolean;
  onDismissAnswerError?: () => void;
}

const IncomingCallContent: React.FC<IIncomingCallContentProps> = ({
  call,
  onAnswer,
  onReject,
  answerError = null,
  answerLoading = false,
  onDismissAnswerError,
}) => {

  const answerFeedback =
    answerError || answerLoading ? (
      <div className={styles.answerFeedback}>
        {answerLoading && (
          <div className={styles.answerLoadingRow} aria-live="polite">
            <span className={styles.answerLoadingDot} aria-hidden />
            <span className={styles.answerLoadingText}>{t("CC_ANSWER_LOADING")}</span>
          </div>
        )}
        {answerError ? (
          <div className={styles.answerErrorBanner} role="alert">
            <span className={styles.answerErrorIcon} aria-hidden>
              !
            </span>
            <p className={styles.answerErrorText}>{answerError}</p>
            {onDismissAnswerError ? (
              <button
                type="button"
                className={styles.answerErrorDismiss}
                onClick={onDismissAnswerError}
                aria-label={t("CLOSE")}
              >
                ×
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    ) : null;

  // Identified caller variant (Purple/Blue)
  if (call.isIdentified && call.callerInfo) {
    const {
      userId,
      firstName,
      lastName,
      phone,
      currentCycle,
      cycle: mvoiceCycle,
      referralNumber,
      status,
      pendingQuestionnaire,
    } = call.callerInfo;
    const displayCycle = mvoiceCycle ?? currentCycle;

    return (
      <div dir={i18n.dir()} className={styles.container} data-variant="identified">
        <div className={`${styles.header} ${styles.headerIdentified}`}>
          <span className={styles.headerText}>
            {t("INCOMING_CALL")} 📞
          </span>
        </div>

        <div className={styles.content}>
          <div className={styles.callerCard}>
            <div className={styles.callerRow}>
              <div className={styles.callerMain}>
                <h3 className={styles.patientName}>
                  {firstName} {lastName}
                </h3>
                <div className={styles.phone}>{phone}</div>
                {/* <div className={styles.badges}>
                  {status && (
                    <span className={`${styles.badge} ${styles.badgeStatus}`}>
                      {status}
                    </span>
                  )}
                  {referralNumber && (
                    <span className={`${styles.badge} ${styles.badgeReferral}`}>
                      {t("REFERRAL")} {referralNumber}
                    </span>
                  )}
                </div> */}
              </div>
              <div className={styles.profileIcon} aria-hidden>
                <span className={styles.profileIconText}>
                  {firstName.charAt(0)}
                  {lastName.charAt(0)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>{t("CYCLE")}</div>
              <div className={styles.infoValue}>{displayCycle}</div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>{t("USERS_ID")}</div>
              <div className={styles.infoValue}>{userId}</div>
            </div>
          </div>

          {/* Above the transfer notice, not below it: this is the thing the therapist has to
              act on during the call, and it competes with a box they have learned to skip.
              Compact because the ringing modal is already dense and has seconds to be read. */}
          <HotlineQuestionnaireNotice pending={pendingQuestionnaire} compact />

          <div className={styles.highlightBox}>
            <span className={styles.highlightIcon} aria-hidden>
              {displayCycle}
            </span>
            <span className={styles.highlightText}>
              {t("INCOMING_REFERRAL_TRANSFER_ON_ANSWER")}
            </span>
          </div>

          {answerFeedback}

          {/* Buttons */}
          <div className={styles.buttons}>
            <button
              className={`${styles.button} ${styles.buttonReject}`}
              onClick={onReject}
            >
              ❌ {t("REJECT")}
            </button>
            <button
              className={`${styles.button} ${styles.buttonAnswer}`}
              onClick={onAnswer}
              disabled={answerLoading}
            >
              📞 {t("CC_ANSWER")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Unidentified caller variant (Orange)
  return (
    <div dir={i18n.dir()} className={styles.container} data-variant="unidentified">
      <div className={`${styles.header} ${styles.headerUnidentified}`}>
        <span className={styles.headerText}>
          {t("INCOMING_CALL")} 📞
        </span>
      </div>

      <div className={styles.content}>
        {/* Warning Box */}
        <div className={styles.warningBox}>
          <div className={styles.warningIcon}>⚠️</div>
          <div className={styles.warningContent}>
            <div className={styles.warningTitle}>
              {t("UNIDENTIFIED_CALLER")}
            </div>
            <div className={styles.warningPhone}>{call.cli}</div>
            {call.cNumberName && (
              <div className={styles.warningPbxName}>{call.cNumberName}</div>
            )}
          </div>
          <div className={styles.questionIcon}>❓</div>
        </div>

        {/* Info Message */}
        <div className={styles.infoMessage}>
          <p className={styles.infoText}>
            {t("ANSWER_TO_COLLECT_DETAILS")}
          </p>
        </div>

        {answerFeedback}

        {/* Buttons */}
        <div className={styles.buttons}>
          <button
            className={`${styles.button} ${styles.buttonReject}`}
            onClick={onReject}
          >
            {t("REJECT")}
          </button>
          <button
            className={`${styles.button} ${styles.buttonAnswer}`}
            onClick={onAnswer}
            disabled={answerLoading}
          >
            📞 {t("ANSWER")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallContent;
